import * as Dropdown from '@radix-ui/react-dropdown-menu';
import type { FilterState, Me, MondayConnectionStatus, ViewMode } from '@mona-roadmap/domain';
import { ChevronDown, ChevronsDownUp, ChevronsUpDown, Filter, LogOut, Moon, Plus, RefreshCw, RotateCcw, Search, Sun, X, Zap } from 'lucide-react';
import type { RefObject } from 'react';
import { useNow } from '../hooks/useNow';
import type { Theme } from '../hooks/useTheme';
import type { ColumnKey } from '../lib/columns';
import { resetPatch, type FilterLabel } from '../lib/filterLabels';
import { formatTime, timeAgo } from '../lib/format';
import { MonaLogo } from './ui/MonaLogo';

export type NewKind = 'deliverable' | 'goal' | 'epic';

interface Props {
  view: ViewMode;
  onViewChange: (v: ViewMode) => void;
  filters: FilterState;
  onSearch: (q: string) => void;
  /** Return keyboard focus to the table. */
  onFocusTable: () => void;
  filterLabels: FilterLabel[];
  matched: number;
  total: number;
  filterButtonRef: RefObject<HTMLButtonElement | null>;
  onOpenFilter: (column: ColumnKey | null, anchor: HTMLElement) => void;
  onPatchFilters: (p: Partial<FilterState>) => void;
  onClearFilters: () => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  /** The user has reordered or resized columns. */
  columnsCustomized: boolean;
  onResetColumns: () => void;
  onRefresh: () => void;
  refreshing: boolean;
  /** True while any roadmap fetch (manual or automatic) is running. */
  fetching: boolean;
  fetchedAt: string | null;
  cached: boolean;
  onNew: (kind: NewKind) => void;
  me: Me;
  onSignOut: () => void;
  theme: Theme;
  onToggleTheme: () => void;
  mondayStatus: MondayConnectionStatus | undefined;
  onConnectMonday: () => void;
  connectingMonday: boolean;
}

const Kbd = ({ children }: { children: string }) => <kbd className="kbd ml-1">{children}</kbd>;

export function Header(p: Props) {
  const now = useNow();
  const columnFilters = p.filterLabels.filter((l) => l.key !== 'q');
  const filtering = p.filterLabels.length > 0;

  return (
    <header className="flex-none border-b border-line bg-panel">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5">
        <div className="flex items-center gap-2 pr-1">
          <MonaLogo className="h-[19px] w-auto text-ink" />
          <span className="text-[17px] font-semibold tracking-tight text-dim" aria-hidden>
            Roadmap
          </span>
          <h1 className="sr-only">Mona Roadmap</h1>
        </div>

        <div role="tablist" aria-label="View" className="flex rounded-md border border-line-strong bg-app p-0.5">
          {(
            [
              ['by-goal', 'By Product Goal'],
              ['by-epic', 'By Epic'],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              role="tab"
              type="button"
              aria-selected={p.view === v}
              className={`h-7 rounded px-3 text-[14px] font-medium ${p.view === v ? 'bg-accent text-white' : 'text-dim hover:text-ink'}`}
              onClick={() => p.onViewChange(v)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] max-w-[380px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-faint" aria-hidden />
            <input
              id="roadmap-search"
              type="search"
              className="input !pl-8 !pr-8"
              placeholder="Search names or IDs (MON-017)  ( / )"
              aria-label="Search"
              value={p.filters.q}
              onChange={(e) => p.onSearch(e.target.value)}
              onKeyDown={(e) => {
                // Enter / Escape / Arrow Down leave the search box and go to the results
                if (e.key === 'Enter' || e.key === 'Escape' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  p.onFocusTable();
                }
              }}
            />
            {p.filters.q ? (
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-faint hover:text-ink" aria-label="Clear search" onClick={() => p.onSearch('')}>
                <X className="size-4" />
              </button>
            ) : null}
          </div>

          <button
            ref={p.filterButtonRef}
            type="button"
            className={`btn ${columnFilters.length > 0 ? '!border-accent !text-accent-ink' : ''}`}
            aria-label="Add filter"
            title="Filter by a column (F)"
            onClick={(e) => p.onOpenFilter(null, e.currentTarget)}
          >
            <Filter className="size-4" aria-hidden /> Filter <Kbd>F</Kbd>
          </button>

          {filtering ? (
            <div className="flex min-w-0 flex-wrap items-center gap-1.5" data-testid="filter-summary" role="group" aria-label="Active filters">
              {columnFilters.map((l) => (
                <span key={l.key} className="inline-flex items-center rounded-full border border-line-strong bg-app text-[13.5px] text-dim" data-testid="filter-chip">
                  <button
                    type="button"
                    className="max-w-[260px] truncate rounded-l-full py-0.5 pl-2.5 pr-1 hover:text-ink"
                    title="Edit this filter (Enter) · Delete removes it"
                    onClick={(e) => p.onOpenFilter(l.column, e.currentTarget)}
                    onKeyDown={(e) => {
                      if (e.key === 'Delete' || e.key === 'Backspace') {
                        e.preventDefault();
                        p.onPatchFilters(resetPatch(l.key));
                        p.onFocusTable();
                      }
                    }}
                  >
                    {l.text}
                  </button>
                  <button type="button" className="mr-1 grid size-5 place-items-center rounded-full hover:bg-hover hover:text-ink" aria-label={`Remove filter ${l.text}`} onClick={() => {
                    p.onPatchFilters(resetPatch(l.key));
                    p.onFocusTable();
                  }}>
                    <X className="size-3.5" aria-hidden />
                  </button>
                </span>
              ))}
              <button
                type="button"
                className="btn btn-ghost !h-7 !px-2"
                onClick={() => {
                  p.onClearFilters();
                  p.onFocusTable();
                }}
              >
                Clear All Filters
              </button>
            </div>
          ) : null}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-stretch">
            <button type="button" className="btn btn-primary !rounded-r-none" onClick={() => p.onNew('deliverable')}>
              <Plus className="size-4" aria-hidden /> New Deliverable
            </button>
            <Dropdown.Root>
              <Dropdown.Trigger asChild>
                <button type="button" className="btn btn-primary !w-8 !justify-center !rounded-l-none !border-l !border-white/20 !px-0" aria-label="More create options">
                  <ChevronDown className="size-4" aria-hidden />
                </button>
              </Dropdown.Trigger>
              <Dropdown.Portal>
                <Dropdown.Content className="pop min-w-[200px] p-1" align="end" sideOffset={4}>
                  <Dropdown.Item className="menu-item" onSelect={() => p.onNew('goal')}>
                    New Product Goal
                  </Dropdown.Item>
                  <Dropdown.Item className="menu-item" onSelect={() => p.onNew('epic')}>
                    New Epic
                  </Dropdown.Item>
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
          </div>

          {p.me.role === 'admin' ? <MondayIndicator status={p.mondayStatus} onConnect={p.onConnectMonday} connecting={p.connectingMonday} /> : null}
          <button
            type="button"
            className="btn btn-icon"
            onClick={p.onToggleTheme}
            aria-label={p.theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            title={`${p.theme === 'dark' ? 'Light' : 'Dark'} theme (T)`}
            data-testid="theme-toggle"
          >
            {p.theme === 'dark' ? <Sun className="size-4" aria-hidden /> : <Moon className="size-4" aria-hidden />}
          </button>
          <UserMenu me={p.me} onSignOut={p.onSignOut} theme={p.theme} onToggleTheme={p.onToggleTheme} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-1.5">
        <button type="button" className="btn btn-ghost" onClick={p.onExpandAll} title="Expand all (E)">
          <ChevronsUpDown className="size-4" aria-hidden /> Expand All <Kbd>E</Kbd>
        </button>
        <button type="button" className="btn btn-ghost" onClick={p.onCollapseAll} title="Collapse all (C)">
          <ChevronsDownUp className="size-4" aria-hidden /> Collapse All <Kbd>C</Kbd>
        </button>
        {p.columnsCustomized ? (
          <button type="button" className="btn btn-ghost" onClick={p.onResetColumns} title="Put the columns back in their default order and widths" data-testid="reset-columns">
            <RotateCcw className="size-4" aria-hidden /> Reset columns
          </button>
        ) : null}
        <span className="h-5 w-px bg-line-strong" aria-hidden />
        <button type="button" className="btn btn-ghost" onClick={p.onRefresh} disabled={p.refreshing} aria-label="Refresh from monday">
          <RefreshCw className={`size-4 ${p.refreshing || p.fetching ? 'animate-spin' : ''}`} aria-hidden /> Refresh <Kbd>R</Kbd>
        </button>
        {p.fetchedAt ? (
          <span
            className="flex items-center gap-1.5 text-[13.5px] text-faint"
            data-testid="updated-at"
            title={`Last read from monday at ${formatTime(p.fetchedAt)}. Refreshes by itself about every 45 seconds while this tab is open, and whenever you return to it.`}
          >
            <span className={`size-2 rounded-full ${p.fetching ? 'animate-pulse bg-accent' : 'bg-ok'}`} aria-hidden />
            {p.fetching ? 'Updating...' : `Updated ${timeAgo(p.fetchedAt, now)}`}
          </span>
        ) : null}
        {filtering ? (
          <span className="ml-auto text-[13.5px] text-faint" data-testid="match-count">
            {p.matched} of {p.total} deliverables match. Counts reflect active filters.
          </span>
        ) : null}
      </div>
    </header>
  );
}

function MondayIndicator({ status, onConnect, connecting }: { status: MondayConnectionStatus | undefined; onConnect: () => void; connecting: boolean }) {
  if (!status) return <span className="text-[13.5px] text-faint">monday...</span>;
  if (status.connected) {
    const title = `Connected${status.connectedBy ? ` by ${status.connectedBy}` : ''}${status.expiresAt ? `, token expires ${status.expiresAt}` : ''}`;
    return (
      <span className="flex items-center gap-1.5 text-[13.5px] text-dim" title={title} data-testid="monday-status">
        <span className="size-2 rounded-full bg-ok" aria-hidden />
        monday connected
      </span>
    );
  }
  return (
    <span className="flex items-center gap-2" data-testid="monday-status">
      <span className="flex items-center gap-1.5 text-[13.5px] text-danger">
        <span className="size-2 rounded-full bg-danger" aria-hidden />
        monday not connected
      </span>
      <button type="button" className="btn" onClick={onConnect} disabled={connecting}>
        <Zap className="size-4" aria-hidden /> Connect monday
      </button>
    </span>
  );
}

function UserMenu({ me, onSignOut, theme, onToggleTheme }: { me: Me; onSignOut: () => void; theme: Theme; onToggleTheme: () => void }) {
  const label = me.name ?? me.email;
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button type="button" className="btn btn-ghost !px-1.5" aria-label={`User menu for ${label}`}>
          <span className="grid size-7 place-items-center rounded-full bg-accent-soft text-[13px] font-semibold text-accent-ink" aria-hidden>
            {label.charAt(0).toUpperCase()}
          </span>
        </button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content className="pop min-w-[240px] p-1" align="end" sideOffset={4}>
          <div className="px-2.5 py-2">
            <div className="font-medium">{label}</div>
            <div className="text-[13px] text-faint">
              {me.email} {me.role === 'admin' ? '(admin)' : ''}
            </div>
          </div>
          <Dropdown.Separator className="my-1 h-px bg-line" />
          <Dropdown.Item className="menu-item" onSelect={onToggleTheme}>
            {theme === 'dark' ? <Sun className="size-4" aria-hidden /> : <Moon className="size-4" aria-hidden />}
            {theme === 'dark' ? 'Light theme' : 'Dark theme'}
          </Dropdown.Item>
          <Dropdown.Item className="menu-item" onSelect={onSignOut}>
            <LogOut className="size-4" aria-hidden /> Sign out
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}
